import { useNavigate, useSearch } from '@tanstack/react-router'
import { Skeleton, Space, Steps, Tag } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import {
  RolodexEntriesWithSyncedRefreshesProvider,
  useRolodexEntries,
} from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import { ArrowLeft } from 'lucide-react'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { SEARCH_PARAM_UNIT_ID } from 'routes/(common_search_params)/-searchParams'
import { SEARCH_PARAM_ORG_ID } from 'routes/(global_search_params)/-searchParams'
import { SEARCH_PARAM_THREAD } from 'routes/libra/-searchParams'
import {
  SEARCH_PARAM_DASHBOARD_CONFIG_ID,
  SEARCH_PARAM_GROUP,
} from 'routes/libra-dashboard/-searchParams'
import styled, { createGlobalStyle } from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'
import { RolodexEntryType } from 'types'
import { DEFAULT_DOCUMENT_TITLE } from 'utils/documentTitle'

import { AssetLink } from './assetLink/AssetLink'
import { CommentLayoutProvider } from './comments/CommentLayoutProvider'
import { CommentSidebar } from './comments/CommentSidebar'
import { CommentSidebarToggle } from './comments/CommentSidebarToggle'
import {
  CommentsProvider,
  useComments,
  useReveal,
} from './comments/CommentsProvider'
import { ActiveComponentProvider } from './components/ActiveComponentContext'
import { TabbedWorkspace } from './components/TabbedWorkspace'
import { TicketStatusBanner } from './components/TicketStatusBanner'
import { useDashboardConfigs } from './dashboard/libraDashboardApi'
import {
  DocumentDataProvider,
  useDocumentData,
} from './document/DocumentDataContext'
import { DocumentUIProvider } from './document/DocumentUIContext'
import {
  LibraDocumentProvider,
  useLibraDocument,
} from './document/LibraDocumentContext'
import {
  getWorkflowStepperState,
  WorkflowStepDisplayStatus,
} from './helpers/workflowStepperState'
import { getActionLayoutComponent } from './lifecycle/lifecycleActionComponents'
import {
  TicketLifecycleProvider,
  useTicketLifecycle,
} from './lifecycle/TicketLifecycleContext'
import { LlmDiffLauncher } from './llmDiff/LlmDiffLauncher'
import { TicketSignoffsProvider } from './signoff/TicketSignoffsContext'
import { FfsAssetModelDiffLauncher } from './stepComponents/ffs/assetModelDiff/FfsAssetModelDiffLauncher'
import { getReviewedStepIds } from './stepComponents/niiReview/niiReviewConfig'
import {
  StepNavigationProvider,
  useStepNavigation,
} from './StepNavigationContext'
import { StepValidationProvider } from './StepValidationContext'
import {
  LibraTicket,
  UpdateTicketArgs,
  useLibraGroups,
} from './ticket/libraTicketsApi'
import { TicketAccessProvider } from './ticket/TicketAccessContext'
import { TicketStepContextProvider } from './ticket/TicketStateContext'
import { TicketWriterProvider } from './ticket/TicketWriterContext'
import { useInactiveStepIds } from './ticket/useInactiveStepIds'
import { WorkflowScopedProviders } from './workflow/WorkflowScopedProviders'
import { useWorkflow } from './WorkflowContext'
import { WorkspaceLayoutRenderer } from './workspace/WorkspaceLayoutRenderer'
import { WorkspaceLayoutConfig } from './workspace/workspaceLayoutTypes'

export const LibraView: FC<{
  ticket: LibraTicket
  doUpdateTicket: (args: UpdateTicketArgs) => Promise<void>
  isLoading: boolean
  orgId: string | undefined
}> = ({ ticket, doUpdateTicket, isLoading, orgId }) => {
  const unitId = ticket.tags.unit as string | undefined
  const siteId = ticket.tags.site as string | undefined

  if (!unitId || !siteId) {
    console.warn(
      'Invalid site or unit ID on Libra ticket: ',
      ticket.id,
      unitId,
      siteId
    )
  }

  const navigate = useNavigate()
  const initialThreadId = useThreadSearchParam()
  const workflow = useWorkflow()

  const { units } = useRolodexEntries(
    useMemo(
      () =>
        unitId ? { ids: [unitId], type: RolodexEntryType.UNIT } : undefined,
      [unitId]
    ),
    { swrOptions: { revalidateOnFocus: false, compare: isEqual } }
  )
  const assetName = units[0]?.data?.name as string | undefined

  const groupId = ticket.tags[RolodexEntryType.LIBRA_TICKET_GROUP]

  const { groups: ticketGroups } = useLibraGroups(
    useMemo(() => ({ ids: groupId ? [groupId as string] : [] }), [groupId])
  )
  const ticketGroup = ticketGroups[0]
  const ticketGroupOwners = ticketGroup?.owners ?? []

  const { configs } = useDashboardConfigs(
    useMemo(
      () => ({ tags: orgId ? { organization: orgId } : undefined }),
      [orgId]
    )
  )
  const dashboardConfigId = configs[0]?.id

  useEffect(() => {
    if (assetName) {
      document.title = `${assetName} - ${DEFAULT_DOCUMENT_TITLE}`
    }
  }, [assetName])

  const handleBackToDashboard = useCallback(() => {
    if (orgId && dashboardConfigId) {
      navigate({
        to: '/libra-dashboard',
        search: {
          [SEARCH_PARAM_ORG_ID]: orgId,
          [SEARCH_PARAM_DASHBOARD_CONFIG_ID]: dashboardConfigId,
          [SEARCH_PARAM_GROUP]: groupId || undefined,
          [SEARCH_PARAM_UNIT_ID]: unitId || undefined,
        },
      })
    }
  }, [navigate, dashboardConfigId, orgId, groupId, unitId])

  return (
    <RolodexEntriesWithSyncedRefreshesProvider>
      <TicketWriterProvider ticket={ticket} doUpdateTicket={doUpdateTicket}>
        <TicketSignoffsProvider ticket={ticket}>
          <TicketLifecycleProvider ticket={ticket} ticketGroup={ticketGroup}>
            <TicketAccessProvider ticket={ticket} isLoading={isLoading}>
              <WorkflowScopedProviders placement='ticket'>
                <CommentsProvider
                  ticket={ticket}
                  ticketGroupOwners={ticketGroupOwners}
                  initialThreadId={initialThreadId}>
                  <LibraDocumentProvider unitId={unitId}>
                    <DocumentDataProvider unitId={unitId}>
                      <DocumentUIProvider>
                        <StepValidationProvider>
                          <StepNavigationProvider ticket={ticket}>
                            <CommentStepNavigator />
                            <LibraViewWrapper data-libra-print>
                              <LibraPrintOverrides />
                              <HeaderContainer>
                                <Space>
                                  <StyledBackButton
                                    onClick={handleBackToDashboard}
                                    ghost>
                                    <BackButtonWrapper>
                                      <ArrowLeft />
                                      Home
                                    </BackButtonWrapper>
                                  </StyledBackButton>
                                  <HeaderTitle>
                                    {assetName ?? ticket.title}
                                    <HeaderTitleSeparator>
                                      -
                                    </HeaderTitleSeparator>
                                    {workflow.name}
                                    <VersionTag>v{ticket.version}</VersionTag>
                                  </HeaderTitle>
                                </Space>
                                <RightAlignedSpace>
                                  <CommentSidebarToggle />
                                  <AssetLink unitId={unitId} siteId={siteId} />
                                  <LlmDiffLauncher />
                                  <FfsAssetModelDiffLauncher />
                                </RightAlignedSpace>
                              </HeaderContainer>
                              <LibraViewInner
                                ticket={ticket}
                                isLoading={isLoading}
                              />
                            </LibraViewWrapper>
                          </StepNavigationProvider>
                        </StepValidationProvider>
                      </DocumentUIProvider>
                    </DocumentDataProvider>
                  </LibraDocumentProvider>
                </CommentsProvider>
              </WorkflowScopedProviders>
            </TicketAccessProvider>
          </TicketLifecycleProvider>
        </TicketSignoffsProvider>
      </TicketWriterProvider>
    </RolodexEntriesWithSyncedRefreshesProvider>
  )
}

/**
 * Sits inside both CommentsProvider and StepNavigationProvider, above the
 * step-keyed subtree. On each reveal request (a comment breadcrumb click or a
 * `?thread=` deep link) navigates to the anchor's step, unless the active
 * step already renders it inline (the NII review page).
 */
const CommentStepNavigator: FC = () => {
  const { activeStep, navigateToStep } = useStepNavigation()

  useReveal((anchor) => {
    if (activeStep?.id === anchor.stepId) return
    if (getReviewedStepIds(activeStep).includes(anchor.stepId)) return
    navigateToStep(anchor.stepId, { allowInactive: true })
  })

  return null
}

function useThreadSearchParam(): string | undefined {
  const search = useSearch({ strict: false }) as Record<string, unknown>

  return search[SEARCH_PARAM_THREAD] as string | undefined
}

const LibraViewInner: FC<{
  ticket: LibraTicket
  isLoading: boolean
}> = ({ ticket, isLoading }) => {
  const workflow = useWorkflow()
  const { activeStepIndex, setActiveStepIndex, activeStep, maxNavigableIndex } =
    useStepNavigation()
  const { actionGroups, currentStage, isTerminal, lifecycleConfig } =
    useTicketLifecycle()

  const inactiveStepIds = useInactiveStepIds()

  const stepItems = useMemo(
    () =>
      workflow.steps.map((step, index) => {
        const inactive = inactiveStepIds.has(step.id)
        const stepState = getWorkflowStepperState({
          stepIndex: index,
          activeStepIndex,
          maxNavigableIndex,
          workflowComplete: ticket.workflowComplete,
          inactive,
        })
        const statusText = inactive
          ? 'N/A'
          : stepState.status === WorkflowStepDisplayStatus.Finish
            ? 'Completed'
            : stepState.status === WorkflowStepDisplayStatus.Process
              ? 'In progress'
              : 'Not started'
        const accessibleLabels = [statusText, stepState.isActive && 'Viewing']
          .filter(Boolean)
          .join(', ')

        return {
          key: step.id ?? `step-${index}`,
          title: step.name,
          status: stepState.status,
          content: (
            <StepScreenReaderLabels>{accessibleLabels}</StepScreenReaderLabels>
          ),
          disabled: stepState.disabled,
        }
      }),
    [
      activeStepIndex,
      maxNavigableIndex,
      workflow.steps,
      inactiveStepIds,
      ticket.workflowComplete,
    ]
  )

  const { isSidebarVisible } = useComments()

  const lifecycleBanners = useMemo(() => {
    if (!lifecycleConfig || !currentStage) return null

    if (isTerminal || actionGroups.length === 0) {
      return (
        <TicketStatusBanner
          actions={[]}
          ticket={ticket}
          currentStage={currentStage}
          isTerminal={isTerminal}
        />
      )
    }

    return actionGroups.map((group) => {
      const Component =
        getActionLayoutComponent(group.componentKey) ?? TicketStatusBanner
      return (
        <Component
          key={group.componentKey}
          actions={group.actions}
          ticket={ticket}
          currentStage={currentStage}
          isTerminal={isTerminal}
        />
      )
    })
  }, [lifecycleConfig, currentStage, isTerminal, actionGroups, ticket])

  return (
    <StepsAndContentLayout>
      {!isLoading && (
        <>
          <StepsWrapper>
            <Steps
              current={activeStepIndex}
              onChange={(index) => {
                const step = workflow.steps[index]
                if (!step) {
                  console.warn('No step found for index', index)
                  return
                }
                setActiveStepIndex(index)
              }}
              items={stepItems}
            />
          </StepsWrapper>
          {lifecycleBanners}
        </>
      )}
      <LibraViewContent
        ticket={ticket}
        activeStep={activeStep}
        isSidebarVisible={isSidebarVisible}
        isLoading={isLoading}
      />
    </StepsAndContentLayout>
  )
}

const LibraViewContent: FC<{
  ticket: LibraTicket
  activeStep: ReturnType<typeof useStepNavigation>['activeStep']
  isSidebarVisible: boolean
  isLoading: boolean
}> = ({ ticket, activeStep, isSidebarVisible, isLoading }) => {
  const { totalDocuments } = useDocumentData()
  const { selectedDocumentId } = useLibraDocument()

  const showPdfViewer = totalDocuments > 0 && Boolean(selectedDocumentId)

  // Synthesize a default layout for steps that don't declare their own
  // `workspaceLayout`. The shape mirrors the legacy classic shell:
  //   - no PDF selected -> docs nav + step content
  //   - PDF selected   -> docs nav + viewer + step content
  // DocumentNavigationPanel + DocumentViewerPanel collapse together (one
  // 40px strip when collapsed) so the engineer can squeeze the doc
  // surfaces away on wider steps.
  const effectiveLayout = useMemo<WorkspaceLayoutConfig>(() => {
    if (activeStep?.workspaceLayout) return activeStep.workspaceLayout
    return {
      panels: showPdfViewer
        ? [
            {
              component: 'DocumentNavigationPanel',
              weight: 1,
              minWidthRem: 11,
              collapseGroup: 'docs',
            },
            {
              component: 'DocumentViewerPanel',
              weight: 3,
              minWidthRem: 20,
              collapseGroup: 'docs',
            },
            { component: 'ActiveStepContent', weight: 2, minWidthRem: 20 },
          ]
        : [
            {
              component: 'DocumentNavigationPanel',
              weight: 1,
              minWidthRem: 11,
            },
            { component: 'ActiveStepContent', weight: 4, minWidthRem: 20 },
          ],
    }
  }, [activeStep?.workspaceLayout, showPdfViewer])

  // Mount the per-component tabs UI ABOVE the workspace layout only when
  // the ACTIVE step declares it needs component scope (author-set flag on
  // the workflow step; no heuristic inference). Deliberately step-scoped,
  // not workflow-wide: ticket-level steps (e.g. review) must stay untabbed.
  const isTabbed = Boolean(activeStep?.requiresComponentScope)

  return (
    // Active-component tab selection lives ABOVE the step-keyed provider so
    // it survives step navigation (the step key remounts everything below).
    <ActiveComponentProvider>
      <TicketStepContextProvider
        key={activeStep?.id ?? '__none__'}
        ticket={ticket}
        activeStepId={activeStep?.id}>
        <CommentLayoutProvider>
          <ContentWrapper>
            <NPanelHost>
              <NPanelMain>
                {isLoading ? (
                  <Skeleton active paragraph={{ rows: 10 }} />
                ) : isTabbed ? (
                  <TabbedWorkspace>
                    <WorkspaceLayoutRenderer layout={effectiveLayout} />
                  </TabbedWorkspace>
                ) : (
                  <WorkspaceLayoutRenderer layout={effectiveLayout} />
                )}
              </NPanelMain>
              <CommentSidebar visible={isSidebarVisible} />
            </NPanelHost>
          </ContentWrapper>
        </CommentLayoutProvider>
      </TicketStepContextProvider>
    </ActiveComponentProvider>
  )
}

const LibraPrintOverrides = createGlobalStyle`
  @media print {
    :has([data-libra-print]) {
      height: auto !important;
      overflow: visible !important;
    }

    :has([data-libra-print]),
    [data-libra-print],
    [data-libra-print] * {
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
    }
  }
`

const StepsAndContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
`

const LibraViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 0;
`

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 1rem 1rem 1rem;
  min-width: 0;
`

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 0 1 auto;
  min-width: 0;
`

const RightAlignedSpace = styled(Space)`
  margin-left: auto;
`

const HeaderTitleSeparator = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-weight: 400;
`

const VersionTag = styled(Tag)`
  && {
    margin-inline-end: 0;
    font-weight: 600;
  }
`

const ContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
`

const BackButtonWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  transition: color 0.2s ease;

  svg {
    width: 1rem;
    height: 1rem;
    transition: transform 0.2s ease;
  }
`

const StyledBackButton = styled(CantileverButton)`
  @media print {
    &&& {
      display: none;
    }
  }

  && {
    padding: 0.5rem 0.875rem;
    font-size: 0.875rem;
    background: ${({ theme }) =>
      theme.forecast.defaultButton || 'rgba(0, 0, 0, 0.04)'};
    border-radius: 0.5rem;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
      background: ${({ theme }) =>
        theme.forecast.hoverButton || 'rgba(0, 0, 0, 0.08)'};
      border-color: ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};

      ${BackButtonWrapper} {
        color: ${({ theme }) => theme.forecast.activeText};

        svg {
          transform: translateX(-0.1875rem);
        }
      }
    }

    &:active {
      ${BackButtonWrapper} {
        color: ${({ theme }) => theme.forecast.activeText};
      }
    }
  }
`

const StepsWrapper = styled.div`
  flex: 0 0 auto;
  width: 100%;
  padding: 0 1rem 1rem 1rem;

  .ant-steps-item-active .ant-steps-item-icon {
    outline: 0.125rem solid ${({ theme }) => theme.forecast.primary};
    outline-offset: 0.125rem;
  }

  .ant-steps-item-icon-number {
    font-weight: 600;
  }

  .ant-steps-item-process .ant-steps-item-icon {
    border-color: ${({ theme }) => theme.forecast.primary} !important;
    background-color: ${({ theme }) => theme.forecast.primary} !important;
    color: ${({ theme }) => theme.colors.grayscale['100']} !important;
  }

  .ant-steps-item:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.forecast.primary};
    outline-offset: 0.25rem;
    border-radius: 0.25rem;
    z-index: 2;
  }

  .ant-steps-item-finish .ant-steps-item-icon {
    border-color: ${cantileverTheme.colors.success['450']} !important;
    background-color: ${cantileverTheme.colors.success['450']} !important;
    color: #fff !important;
  }

  .ant-steps-item-finish .ant-steps-item-icon-finish {
    color: #fff !important;

    svg {
      stroke: currentColor;
      stroke-width: 32;
      stroke-linejoin: round;
    }
  }
`

const StepScreenReaderLabels = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

const NPanelHost = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  min-height: 0;
  width: 100%;
  overflow: hidden;
`

const NPanelMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
`
